import { addDays } from 'date-fns';
import { sortBy } from 'lodash';
import {
  CalculsFormCalendrierAssistance,
  CalculsGlobal,
} from 'src/constants/calculs';
import { prejudiceBaseDefaultValues } from 'src/constants/prejudice/defaultValues';
import {
  PrejudiceFormCalendrierAssistance,
  PrejudiceFormCalendrierAssistanceRow,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
export type CalendrierAssistanceExistingRow = Partial<PrejudiceFormCalendrierAssistanceRow>;
export type CalendrierAssistanceExistingValues = Omit<
  Partial<PrejudiceFormCalendrierAssistance>,
  'rows'
> & {
  rows?: CalendrierAssistanceExistingRow[];
};

export const defaultTableAssistanceRow = (params: {
  isCalculCapitalisation: boolean;
  existingValues: CalendrierAssistanceExistingValues | undefined;
  existingRow: CalendrierAssistanceExistingRow | undefined;
  type: PrejudiceFormCalendrierAssistanceRow['type'];
  procedure: Procedure;
}): Omit<PrejudiceFormCalendrierAssistanceRow, 'type'> => {
  const { isCalculCapitalisation, existingValues, existingRow, procedure } =
    params;
  const commentaires = existingRow?.commentaires ?? '';
  const notes = existingRow?.notes ?? '';
  const numerosPieces = existingRow?.numerosPieces ?? { rows: [] };
  const dateDebut = existingRow?.dateDebut ?? null;
  const dateFin = existingRow?.dateFin ?? null;
  const joursPeriode =
    dateDebut && dateFin ? CalculsGlobal.getDays(dateDebut, dateFin) : 0;
  const joursHospitalisationADeduire =
    dateDebut && dateFin
      ? CalculsGlobal.getJoursHospitalisationsADeduire(
          dateDebut,
          dateFin,
          procedure.hospitalisations,
        )
      : 0;
  const deduireJoursHospitalisation =
    existingRow?.deduireJoursHospitalisation ?? false;
  const joursRetenus = CalculsFormCalendrierAssistance.getJoursRetenus({
    joursHospitalisationADeduire,
    joursPeriode,
    deduireJoursHospitalisation,
  });
  const joursConges = existingRow?.joursConges ?? 365;
  const totalJours = CalculsFormCalendrierAssistance.totalJours({
    joursRetenus: isCalculCapitalisation ? joursPeriode : joursRetenus,
    joursConges,
  });
  const typeAssistance = existingRow?.typeAssistance ?? '';
  const forfaitHoraire =
    existingRow?.forfaitHoraire || existingValues?.forfaitHoraire || 0;
  const nombreHeureParUniteChoisie =
    existingRow?.nombreHeureParUniteChoisie || 0;
  const uniteNombreHeure = existingRow?.uniteNombreHeure || 'jour';
  const quantite = CalculsFormCalendrierAssistance.quantite({
    totalJours,
    nombreHeureParUniteChoisie,
    uniteNombreHeure,
  });
  const annualiseMontantTotal =
    CalculsFormCalendrierAssistance.annualiseMontantTotal({
      forfaitHoraire,
      joursConges,
      nombreHeureParUniteChoisie,
      uniteNombreHeure,
    });
  const dateJustificatif =
    existingRow?.dateJustificatif ??
    (!isCalculCapitalisation ? (procedure.dateLiquidation ?? null) : null);
  const montantsDejaRevalorises = existingRow?.montantsDejaRevalorises ?? true;
  const montantTotal = CalculsFormCalendrierAssistance.montantParPeriode({
    annualiseMontant: annualiseMontantTotal,
    totalJours,
    joursConges,
  });

  const priseEnChargeTiersPayeurs = procedure.tiersPayeurs.map(
    (tiersPayeur) => ({
      tiersPayeur,
      montant:
        existingRow?.priseEnChargeTiersPayeurs?.find(
          (priseEnChargeTiersPayeur) =>
            priseEnChargeTiersPayeur.tiersPayeur === tiersPayeur,
        )?.montant || 0,
    }),
  );
  const deductionFiscale = existingRow?.deductionFiscale || 0;
  const autresDeductions = existingRow?.autresDeductions || 0;
  const resteACharge = CalculsFormCalendrierAssistance.resteACharge({
    montantTotal,
    deductionFiscale,
    autresDeductions,
    priseEnChargeTiersPayeurs,
  });

  return {
    notes,
    commentaires,
    numerosPieces,
    dateDebut,
    dateFin,
    joursPeriode,
    joursHospitalisationADeduire,
    deduireJoursHospitalisation,
    joursRetenus,
    joursConges,
    totalJours,
    typeAssistance,
    forfaitHoraire,
    nombreHeureParUniteChoisie,
    uniteNombreHeure,
    dateJustificatif,
    montantsDejaRevalorises,
    montantTotal,
    quantite,
    priseEnChargeTiersPayeurs,
    deductionFiscale,
    autresDeductions,
    resteACharge,
  };
};

export const defaultTableAssistanceRowEchu = (params: {
  isCalculCapitalisation: boolean;
  existingValues: CalendrierAssistanceExistingValues | undefined;
  existingRow: CalendrierAssistanceExistingRow | undefined;
  procedure: Procedure;
}): PrejudiceFormCalendrierAssistanceRow => ({
  ...defaultTableAssistanceRow({ ...params, type: 'echu' }),
  type: 'echu',
});

export const defaultTableAssistanceRowAEchoir = (params: {
  existingValues: CalendrierAssistanceExistingValues | undefined;
  existingRow: CalendrierAssistanceExistingRow | undefined;
  procedure: Procedure;
}): PrejudiceFormCalendrierAssistanceRow => {
  const { procedure, existingValues, existingRow } = params;
  const dateLiquidation = procedure.dateLiquidation
    ? new Date(procedure.dateLiquidation)
    : undefined;
  const defaultDateDebut =
    existingRow?.dateDebut ??
    (dateLiquidation ? addDays(dateLiquidation, 1).toISOString() : null);
  return {
    ...defaultTableAssistanceRow({
      isCalculCapitalisation: true,
      existingValues,
      existingRow: {
        ...existingRow,
        dateDebut: defaultDateDebut,
      },
      procedure,
      type: 'aEchoir',
    }),
    type: 'aEchoir',
  };
};

export const calendrierAssistanceDefaultValues = ({
  procedure,
  values,
  isExtractedValues,
}: {
  procedure: Procedure;
  values: CalendrierAssistanceExistingValues | undefined;
  isExtractedValues: boolean;
}): PrejudiceFormCalendrierAssistance => {
  const baseValues = prejudiceBaseDefaultValues({
    values,
  });
  const notes = values?.notes || '';
  const forfaitHoraire =
    (values && !isExtractedValues ? values?.forfaitHoraire : procedure.baremes?.forfaitHoraireATPT) || 0;
  const isDFTPRowsAdded = values?.isDFTPRowsAdded || false;
  const revalorisationCoefficientsType =
    values?.revalorisationCoefficientsType || 'smic';
  const existingValues = {
    ...baseValues,
    notes,
    forfaitHoraire,
    isDFTPRowsAdded,
    revalorisationCoefficientsType,
  };
  const rows = sortBy(
    values?.rows?.map((row) => ({
      ...defaultTableAssistanceRowEchu({
        isCalculCapitalisation: false,
        existingValues,
        existingRow: row,
        procedure,
      }),
    })) || [],
    ['dateDebut', 'typeAssistance'],
  );
  return {
    ...baseValues,
    notes,
    forfaitHoraire,
    isDFTPRowsAdded: values?.isDFTPRowsAdded || false,
    revalorisationCoefficientsType:
      values?.revalorisationCoefficientsType || 'smic',
    rows,
  };
};
